import React, { useRef, useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import {
  FormControl,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';

import { ButtonPill } from 'components/shared/MuiExtensions';
import Stack from 'components/shared/Stack';
import TruncatedContent from 'components/shared/TruncatedContent';

import { RatingFeedback, RatingFeedbackResponse } from 'typings/api/concierge';
import { formatDate } from 'utils/functions';

interface FeedbackResponse {
  label: string;
  value: string;
}

interface FeedbackResponseData {
  feedbackResponse: string;
  feedbackResponseComment?: string;
}

interface ConciergeRatingsReviewProps {
  rating: RatingFeedback[];
  disableDoneAction?: boolean;
  onFeedbackResponse: (arg: RatingFeedbackResponse) => void;
}

/* HELPERS */
const columnHeadings = [
  'date',
  'type',
  'rating',
  'customer name',
  'rating note',
  'transcript',
  'link',
  'feedback',
  '',
];

const feedbackResponses: FeedbackResponse[] = [
  { label: 'need more info', value: 'need more info' },
  { label: 'feedback acknowledged', value: 'feedback acknowledged' },
  { label: 'other', value: 'other' },
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableContainer: {
      maxHeight: '75vh',
    },
    tableHeading: {
      background: theme.palette.background.default,
    },
    tableCell: {
      verticalAlign: 'top',
    },
    tableCellFeedbackResponse: {
      width: '30ch',
      maxWidth: '30ch',
      verticalAlign: 'top',
    },
    feedbackField: {
      padding: theme.spacing(1),
      background: theme.palette.background.default,
    },
    feedbackFieldMenu: {
      background: theme.palette.common.white,
      color: theme.palette.common.black,
    },
    feedbackFieldMenuItem: {
      '&:hover': {
        background: theme.palette.grey[500],
        color: theme.palette.common.white,
      },
    },
    feedbackCommentField: {
      background: theme.palette.background.default,
    },
    feedbackCommentInputField: {
      borderRadius: 0,
    },
  })
);

/* COMPONENTS */
function ConciergeRatingsReview({
  rating,
  disableDoneAction,
  onFeedbackResponse,
}: ConciergeRatingsReviewProps) {
  const responsesRef = useRef<Record<string, FeedbackResponseData>>({});
  const [responses, setResponses] = useState(0);
  const classes = useStyles();

  const handleDoneBtnClick = (ratingId: number) => {
    return () => {
      const payload: RatingFeedbackResponse = {
        conciergeRatingId: ratingId,
        ratingFeedbackResponse: responsesRef.current[ratingId].feedbackResponse,
      };

      const feedbackResponseComment = responsesRef.current[ratingId].feedbackResponseComment;
      if (feedbackResponseComment) {
        payload.ratingFeedbackResponseComment = feedbackResponseComment;
      }

      onFeedbackResponse(payload);
    };
  };

  const handleSelectFeedbackResponse = (ratingId: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const ratingFeedbackResponse = event.target.value;

      if (!ratingFeedbackResponse) {
        delete responsesRef.current[ratingId];
        setResponses(responses - 1);
        return;
      }

      if (!responsesRef.current[ratingId]) {
        responsesRef.current[ratingId] = {
          feedbackResponse: '',
          feedbackResponseComment: '',
        };
      }

      responsesRef.current[ratingId].feedbackResponse = ratingFeedbackResponse;
      setResponses(responses + 1);
    };
  };

  const handleFeedbackResponseComment = (ratingId: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      responsesRef.current[ratingId].feedbackResponseComment = event.target.value;
    };
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table stickyHeader aria-label="Concierge Feedback Review Table">
        <TableHead>
          <TableRow>
            {columnHeadings.map((columnHeading: string) => (
              <TableCell classes={{ root: classes.tableHeading }} variant="head" align="center">
                <Typography variant="h6" color="primary">
                  {columnHeading.toLocaleLowerCase()}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rating.map((fdbk) => (
            <TableRow key={fdbk.id}>
              <TableCell className={classes.tableCell} component="th" scope="row">
                {formatDate(fdbk.feedbackCompletedAt, 'MM/DD/YY')}
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                {fdbk.commType}
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                {fdbk.sentiment}
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                {fdbk.customer}
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                {fdbk.note}
              </TableCell>
              <TableCell className={classes.tableCell} align="left">
                <TruncatedContent truncate>{fdbk.commContent}</TruncatedContent>
              </TableCell>
              <TableCell className={classes.tableCell} align="left">
                <Link href={`/${fdbk.leadId}`} target="_blank" rel="noopener noreferrer">
                  view lead
                </Link>
              </TableCell>
              <TableCell className={classes.tableCellFeedbackResponse} align="left">
                <Stack>
                  <FormControl fullWidth>
                    <Paper square classes={{ root: classes.feedbackField }}>
                      <TextField
                        select
                        fullWidth
                        InputProps={{ disableUnderline: true }}
                        SelectProps={{
                          displayEmpty: true,
                          MenuProps: {
                            PaperProps: {
                              square: true,
                              className: classes.feedbackFieldMenu,
                            },
                            getContentAnchorEl: null,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'center',
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'center',
                            },
                          },
                        }}
                        onChange={handleSelectFeedbackResponse(fdbk.id)}
                        defaultValue=""
                      >
                        <MenuItem
                          value=""
                          classes={{ root: classes.feedbackFieldMenuItem }}
                          disabled
                        >
                          Select an option
                        </MenuItem>
                        {feedbackResponses.map((response) => (
                          <MenuItem
                            value={response.value}
                            classes={{ root: classes.feedbackFieldMenuItem }}
                          >
                            {response.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Paper>
                  </FormControl>
                  {responsesRef.current[fdbk.id]?.feedbackResponse === 'other' && (
                    <FormControl fullWidth>
                      <Paper elevation={0} square classes={{ root: classes.feedbackCommentField }}>
                        <TextField
                          multiline
                          minRows={5}
                          variant="outlined"
                          InputProps={{
                            classes: { root: classes.feedbackCommentInputField },
                          }}
                          onChange={handleFeedbackResponseComment(fdbk.id)}
                        />
                      </Paper>
                    </FormControl>
                  )}
                </Stack>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <ButtonPill
                  disabled={!responsesRef.current[fdbk.id]?.feedbackResponse || disableDoneAction}
                  onClick={handleDoneBtnClick(fdbk.id)}
                >
                  done
                </ButtonPill>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ConciergeRatingsReview;
